import React from "react";

// Lightweight block/inline renderer for blog post `content` (a plain string
// typed into a textarea in the admin — see AdminPages.jsx's BlogAdmin). Not
// a full Markdown engine: just the handful of patterns an admin actually
// types (##/### headings, - lists, 1. lists, > quotes, --- rules,
// ![alt](url) images, **bold**/*italic*/`code`/[text](url) inline, and a
// bare YouTube or Instagram URL on its own line auto-embeds as a real
// player) — enough to turn a wall of plain paragraphs into a real article
// without asking the admin to learn real Markdown or changing the CMS
// schema. Deliberately builds React elements (not dangerouslySetInnerHTML)
// so admin-authored content can never inject arbitrary HTML/script.

const YOUTUBE_RE = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/i;
const INSTAGRAM_RE = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/(p|reel|reels)\/([\w-]+)/i;
const IMAGE_RE = /^!\[([^\]]*)\]\(([^)]+)\)$/;
const HR_RE = /^-{3,}$/;
const UL_RE = /^[-*]\s+/;
const OL_RE = /^\d+\.\s+/;
const QUOTE_RE = /^>\s?/;

function isSpecialLine(line) {
  return (
    YOUTUBE_RE.test(line) ||
    INSTAGRAM_RE.test(line) ||
    IMAGE_RE.test(line) ||
    HR_RE.test(line) ||
    UL_RE.test(line) ||
    OL_RE.test(line) ||
    QUOTE_RE.test(line) ||
    line.startsWith("## ") ||
    line.startsWith("### ")
  );
}

// **bold**, *italic*, `code`, [text](url) — order matters (links checked
// first so their brackets/parens never get mistaken for emphasis markers).
const INLINE_RE = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\*([^*]+)\*)/;

function renderInline(text, keyPrefix) {
  const nodes = [];
  let remaining = text;
  let key = 0;
  while (remaining.length) {
    const m = INLINE_RE.exec(remaining);
    if (!m) {
      nodes.push(remaining);
      break;
    }
    if (m.index > 0) nodes.push(remaining.slice(0, m.index));
    if (m[1]) {
      nodes.push(
        <a
          key={`${keyPrefix}-${key++}`}
          href={m[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#F7941D] font-semibold underline decoration-2 underline-offset-2 hover:text-[#e0861a]"
        >
          {m[2]}
        </a>
      );
    } else if (m[4]) {
      nodes.push(
        <strong key={`${keyPrefix}-${key++}`} className="font-bold text-[#1a3c5e]">
          {m[5]}
        </strong>
      );
    } else if (m[6]) {
      nodes.push(
        <code key={`${keyPrefix}-${key++}`} className="px-1.5 py-0.5 rounded bg-[#FEF3E2] text-[#1a3c5e] text-[0.9em] font-mono">
          {m[7]}
        </code>
      );
    } else if (m[8]) {
      nodes.push(
        <em key={`${keyPrefix}-${key++}`} className="italic">
          {m[9]}
        </em>
      );
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  return nodes;
}

function YouTubeEmbed({ id }) {
  return (
    <div className="my-8 rounded-2xl overflow-hidden shadow-lg border border-[#1a3c5e]/10 aspect-video bg-black">
      <iframe
        className="w-full h-full"
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        title="YouTube video"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function InstagramEmbed({ kind, id }) {
  return (
    <div className="my-8 flex justify-center">
      <div className="w-full max-w-[400px] rounded-2xl overflow-hidden shadow-lg border border-[#1a3c5e]/10 bg-white">
        <iframe
          src={`https://www.instagram.com/${kind}/${id}/embed`}
          title="Instagram post"
          loading="lazy"
          className="w-full"
          style={{ height: 620, border: 0 }}
          scrolling="no"
          allowFullScreen
        />
      </div>
    </div>
  );
}

export default function RichContent({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    const ytMatch = trimmed.match(YOUTUBE_RE);
    if (ytMatch) {
      blocks.push(<YouTubeEmbed key={key++} id={ytMatch[1]} />);
      i++;
      continue;
    }

    const igMatch = trimmed.match(INSTAGRAM_RE);
    if (igMatch) {
      blocks.push(<InstagramEmbed key={key++} kind={igMatch[1] === "reels" ? "reel" : igMatch[1]} id={igMatch[2]} />);
      i++;
      continue;
    }

    const imgMatch = trimmed.match(IMAGE_RE);
    if (imgMatch) {
      blocks.push(
        <figure key={key++} className="my-8">
          <img src={imgMatch[2]} alt={imgMatch[1]} className="w-full rounded-2xl shadow-md" loading="lazy" />
          {imgMatch[1] && <figcaption className="text-center text-sm text-gray-500 mt-2 italic">{imgMatch[1]}</figcaption>}
        </figure>
      );
      i++;
      continue;
    }

    if (HR_RE.test(trimmed)) {
      blocks.push(<hr key={key++} className="my-10 border-t-2 border-[#1a3c5e]/10" />);
      i++;
      continue;
    }

    if (trimmed.startsWith("### ")) {
      blocks.push(
        <h3 key={key++} className="font-display text-xl sm:text-2xl font-bold text-[#1a3c5e] mt-8 mb-3">
          {renderInline(trimmed.slice(4), `h3-${key}`)}
        </h3>
      );
      i++;
      continue;
    }

    if (trimmed.startsWith("## ")) {
      blocks.push(
        <h2 key={key++} className="font-display text-2xl sm:text-3xl font-bold text-[#1a3c5e] mt-10 mb-4 pb-2 border-b-2 border-[#F7941D]/20">
          {renderInline(trimmed.slice(3), `h2-${key}`)}
        </h2>
      );
      i++;
      continue;
    }

    if (QUOTE_RE.test(trimmed)) {
      const items = [];
      while (i < lines.length && QUOTE_RE.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(QUOTE_RE, ""));
        i++;
      }
      const qKey = key++;
      blocks.push(
        <blockquote key={qKey} className="my-8 border-l-4 border-[#F7941D] bg-[#FEF3E2] rounded-r-2xl px-6 py-4 italic text-[#1a3c5e] text-lg">
          {items.map((t, idx) => (
            <p key={idx} className={idx ? "mt-2" : ""}>
              {renderInline(t, `q-${qKey}-${idx}`)}
            </p>
          ))}
        </blockquote>
      );
      continue;
    }

    if (UL_RE.test(trimmed)) {
      const items = [];
      while (i < lines.length && UL_RE.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(UL_RE, ""));
        i++;
      }
      const ulKey = key++;
      blocks.push(
        <ul key={ulKey} className="my-5 space-y-2">
          {items.map((t, idx) => (
            <li key={idx} className="flex gap-2.5 text-gray-700 leading-relaxed">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#F7941D] flex-shrink-0" />
              <span>{renderInline(t, `ul-${ulKey}-${idx}`)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (OL_RE.test(trimmed)) {
      const items = [];
      while (i < lines.length && OL_RE.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(OL_RE, ""));
        i++;
      }
      const olKey = key++;
      blocks.push(
        <ol key={olKey} className="my-5 space-y-2">
          {items.map((t, idx) => (
            <li key={idx} className="flex gap-3 text-gray-700 leading-relaxed">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1a3c5e] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <span>{renderInline(t, `ol-${olKey}-${idx}`)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Plain paragraph — accumulate consecutive plain lines into one <p>.
    const paraLines = [];
    while (i < lines.length && lines[i].trim() && !isSpecialLine(lines[i].trim())) {
      paraLines.push(lines[i].trim());
      i++;
    }
    const pKey = key++;
    blocks.push(
      <p key={pKey} className="text-gray-700 leading-[1.85] text-[17px] mb-5">
        {renderInline(paraLines.join(" "), `p-${pKey}`)}
      </p>
    );
  }

  return <>{blocks}</>;
}
